/**
 * Fait : le formulaire du catalogue — inscrire un modèle, ou retoucher celui
 *   qui existe. À l'inscription on choisit le fournisseur puis le modèle ; à la
 *   retouche, le fournisseur et l'identifiant ne bougent plus, la route ne les
 *   accepte pas.
 *
 *   IL NE POSE PLUS DE DEVINETTE, et c'est tout le sujet de sa réécriture du
 *   2026-08-26. Il demandait un « identifiant technique » dans un champ libre,
 *   avec un seul exemple pour toute aide. Charles, en essayant de s'en servir :
 *   « je choisis par exemple open routeur et c'est pas clair on va où on fait
 *   quoi comment », et « le même problème avec claude comment choisir dans
 *   l'abonnement sonnet opus ou fable ». Un champ libre dont trois valeurs
 *   seulement sont acceptées n'est pas un champ : c'est un piège.
 *
 *   TROIS CHANGEMENTS, ET LE PREMIER SUFFIRAIT PRESQUE. ① L'abonnement Claude
 *   propose sa LISTE au lieu d'un champ — ce sont les seuls modèles que le pont
 *   accepte, autant les montrer. ② La voie de connexion ne se demande plus :
 *   elle se déduit du fournisseur, parce qu'aucun n'en porte deux. ③ Chaque
 *   fournisseur dit OÙ trouver l'identifiant qu'il attend.
 *
 * Ne fait pas : il n'appelle pas le serveur, ne connaît aucun fournisseur qui
 *   ne soit dans la liste partagée, et n'invente aucun prix — un prix qu'on n'a
 *   pas relevé ne se devine pas.
 */

import { useState } from 'react';

import {
  AIDE_DU_FOURNISSEUR,
  FOURNISSEURS,
  MODELES_DE_L_ABONNEMENT,
  VOIE_DU_FOURNISSEUR,
  type Fournisseur,
} from '@aymoai/shared';

import { Champ } from '../gabarits/Champ.tsx';
import { RechercheDeModele } from './RechercheDeModele.tsx';
import type { DemandeDeModele, EntreeDeCatalogueVue, RetoucheDeModele } from './api-argent.ts';
import { enCentimes, enSaisie } from './montants.ts';

/** Le nom que l'on montre pour chaque fournisseur — repris du contrat partagé. */
export const NOM_DU_FOURNISSEUR: Readonly<Record<Fournisseur, string>> = Object.fromEntries(
  FOURNISSEURS.map((nom) => [nom, AIDE_DU_FOURNISSEUR[nom].nom]),
) as Record<Fournisseur, string>;

const SAISIE_ILLISIBLE = 'Écrivez un montant en euros, par exemple 3,00.';

/** Les deux prix, lus ensemble : un seul des deux ne veut rien dire. */
function litLesPrix(
  entree: string,
  sortie: string,
): { readonly entree: number; readonly sortie: number } | null {
  const enJetons = enCentimes(entree);
  const enRetour = enCentimes(sortie);

  return enJetons === null || enRetour === null ? null : { entree: enJetons, sortie: enRetour };
}

export function FormulaireDeModele({
  depart,
  enCours,
  surInscription,
  surRetouche,
  surAbandon,
}: {
  /** L'entrée que l'on retouche. Absente, on en inscrit une neuve. */
  readonly depart?: EntreeDeCatalogueVue;

  readonly enCours: boolean;

  /**
   * Ces deux-là rendent le VERDICT du serveur, pas un accusé de clic : le
   * formulaire ne se referme qu'après un « oui ». Refermer au clic faisait
   * perdre toute la saisie chaque fois que le serveur refusait.
   */
  readonly surInscription: (demande: DemandeDeModele) => Promise<boolean>;
  readonly surRetouche: (entreeId: string, demande: RetoucheDeModele) => Promise<boolean>;
  readonly surAbandon: () => void;
}) {
  const [fournisseur, setFournisseur] = useState<Fournisseur>(depart?.fournisseur ?? 'claude_code');
  const [identifiant, setIdentifiant] = useState(depart?.identifiantModele ?? 'sonnet');
  const [libelle, setLibelle] = useState(depart?.libelle ?? MODELES_DE_L_ABONNEMENT.sonnet);

  const [prixEntree, setPrixEntree] = useState(
    depart?.prixEntreeCentimesParMillionDeJetons === undefined
      ? ''
      : enSaisie(depart.prixEntreeCentimesParMillionDeJetons),
  );
  const [prixSortie, setPrixSortie] = useState(
    depart?.prixSortieCentimesParMillionDeJetons === undefined
      ? ''
      : enSaisie(depart.prixSortieCentimesParMillionDeJetons),
  );

  const [erreur, setErreur] = useState<string | null>(null);

  const voie = depart?.voie ?? VOIE_DU_FOURNISSEUR[fournisseur];
  const surAbonnement = voie === 'abonnement';
  const dansUneListe = fournisseur === 'claude_code';

  /*
   * CHANGER DE FOURNISSEUR REMET LE MODÈLE À ZÉRO. Sans cela on gardait
   * l'identifiant du précédent — « sonnet » resté sélectionné après un passage
   * à OpenRouter, par exemple — et l'on inscrivait un modèle qui n'existe pas
   * chez ce fournisseur-là. Le serveur l'aurait accepté : c'est au premier
   * appel réel que ça aurait cassé.
   */
  function changeDeFournisseur(neuf: Fournisseur) {
    setFournisseur(neuf);
    setErreur(null);

    if (neuf === 'claude_code') {
      setIdentifiant('sonnet');
      setLibelle(MODELES_DE_L_ABONNEMENT.sonnet);
    } else {
      setIdentifiant('');
      setLibelle('');
    }
  }

  function envoie() {
    const prix = surAbonnement ? { entree: 0, sortie: 0 } : litLesPrix(prixEntree, prixSortie);

    if (prix === null) {
      setErreur(SAISIE_ILLISIBLE);
      return;
    }

    setErreur(null);

    if (depart !== undefined) {
      void surRetouche(depart.id, {
        version: depart.version,
        libelle,
        ...(surAbonnement
          ? {}
          : {
              prixEntreeCentimesParMillionDeJetons: prix.entree,
              prixSortieCentimesParMillionDeJetons: prix.sortie,
            }),
      });
      return;
    }

    void surInscription(
      surAbonnement
        ? { voie: 'abonnement', fournisseur, identifiantModele: identifiant, libelle }
        : {
            voie: 'cle_api',
            fournisseur,
            identifiantModele: identifiant,
            libelle,
            prixEntreeCentimesParMillionDeJetons: prix.entree,
            prixSortieCentimesParMillionDeJetons: prix.sortie,
          },
    );
  }

  return (
    <form
      data-role="formulaire-de-modele"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        envoie();
      }}
    >
      {depart === undefined && (
        <>
          <p className="champ">
            <label htmlFor="fournisseur-du-modele">Chez qui</label>
            <span className="texte-attenue">
              {surAbonnement
                ? 'Compris dans un abonnement déjà payé : aucun euro ne sera dépensé.'
                : 'Facturé à l’usage : il faudra indiquer ses prix.'}
            </span>
            <select
              id="fournisseur-du-modele"
              data-role="fournisseur-du-modele"
              value={fournisseur}
              onChange={(evenement) => changeDeFournisseur(evenement.target.value as Fournisseur)}
            >
              {FOURNISSEURS.map((nom) => (
                <option key={nom} value={nom}>
                  {NOM_DU_FOURNISSEUR[nom]}
                </option>
              ))}
            </select>
          </p>

          {dansUneListe ? (
            <p className="champ">
              <label htmlFor="identifiant-du-modele">Quel modèle</label>
              <span className="texte-attenue">{AIDE_DU_FOURNISSEUR[fournisseur].ou}</span>
              <select
                id="identifiant-du-modele"
                data-role="identifiant-du-modele"
                value={identifiant}
                onChange={(evenement) => {
                  const choisi = evenement.target.value as keyof typeof MODELES_DE_L_ABONNEMENT;
                  setIdentifiant(choisi);
                  /* Le nom montré suit le choix : on peut le réécrire juste
                     après, mais on ne part jamais d'un champ vide. */
                  setLibelle(MODELES_DE_L_ABONNEMENT[choisi]);
                }}
              >
                {Object.entries(MODELES_DE_L_ABONNEMENT).map(([alias, dit]) => (
                  <option key={alias} value={alias}>
                    {dit}
                  </option>
                ))}
              </select>
            </p>
          ) : (
            <>
              {fournisseur === 'openrouter' && (
                /* CHEZ CELUI QUI SERT DES CENTAINES DE MODÈLES, ON CHERCHE.
                   Le choix remplit l'identifiant ET les deux prix, déjà
                   convertis en euros — trois recopies en moins, dont deux qui
                   faisaient compter le budget faux sans rien dire (`D-036`). */
                <RechercheDeModele
                  surChoix={(modele) => {
                    setIdentifiant(modele.identifiantModele);
                    setLibelle(modele.nom);
                    setPrixEntree(enSaisie(modele.prixEntreeCentimesParMillionDeJetons));
                    setPrixSortie(enSaisie(modele.prixSortieCentimesParMillionDeJetons));
                    setErreur(null);
                  }}
                />
              )}

              <Champ
                libelle="Identifiant du modèle, chez ce fournisseur"
                aide={AIDE_DU_FOURNISSEUR[fournisseur].ou}
                type="text"
                name="identifiant-du-modele"
                data-role="identifiant-du-modele"
                value={identifiant}
                onChange={(evenement) => setIdentifiant(evenement.target.value)}
              />
            </>
          )}
        </>
      )}

      <Champ
        libelle="Nom montré à la famille"
        aide="Le nom que chacun verra dans le choix du modèle."
        type="text"
        name="libelle-du-modele"
        data-role="libelle-du-modele"
        value={libelle}
        onChange={(evenement) => setLibelle(evenement.target.value)}
      />

      {!surAbonnement && (
        <>
          <Champ
            libelle="Prix de ce qu’on envoie, en euros par million de jetons"
            type="text"
            inputMode="decimal"
            name="prix-entree-du-modele"
            data-role="prix-entree-du-modele"
            value={prixEntree}
            {...(erreur === null ? {} : { erreur })}
            onChange={(evenement) => {
              setPrixEntree(evenement.target.value);
              setErreur(null);
            }}
          />

          <Champ
            libelle="Prix de ce qu’on reçoit, en euros par million de jetons"
            type="text"
            inputMode="decimal"
            name="prix-sortie-du-modele"
            data-role="prix-sortie-du-modele"
            value={prixSortie}
            onChange={(evenement) => {
              setPrixSortie(evenement.target.value);
              setErreur(null);
            }}
          />
        </>
      )}

      <div className="barre-de-boutons">
        <button type="submit" data-role="enregistrer-le-modele" disabled={enCours}>
          {depart === undefined ? 'Inscrire ce modèle' : 'Enregistrer'}
        </button>

        <button type="button" data-role="abandonner-le-modele" onClick={surAbandon}>
          Annuler
        </button>
      </div>
    </form>
  );
}
